/*
 * OGA User Auth Management Service
 * !== 기관 사용자 권한 관리 서비스 ==!
 */

/*
 * @ BASE
 * @ 서비스에서 사용하는 기본 정보 세팅
 * @ path : 화면들의 기본 웹 경로
 * @ api : 사용하는 api 의 기본 주소
 * @ api 주소가 여러개일 경우 _ 로구분하여 의미에 맞게 카멜표기업으로 표시 : api_clr
 * @ nodes : 화면의 Page.Navigation 에서 사용하는 기본 결로 Array
 */
import { ptlEnvs } from "@/tz/go/egaz/osci/ptl/comn";
import { comnUtils } from "@/comn/utils";
import { api } from "@/comn";
import { TFormSchema, TGridSchema } from "@/comn/hooks";

export const BASE = {
    path: `${ptlEnvs.base}/ptl/org-auth`,
    api: `${process.env.REACT_APP_API_PTL_COMN}/api/v1/osci/ptl/ptl/auth/org-auth`,
    nodes: [],
};

/*
 * @ APIS
 * @ 화면에서 사용하는 api 정의
 */
export const APIS = {
    getOgaUsrList: (data: any, page: number, size: number) =>
        api.get(`${BASE.api}?page=${page}&size=${size}`, { params: comnUtils.toGetParams(data) }),
    getAuthList: (tin: any, data: any, page: number, size: number) =>
        api.get(`${BASE.api}/${tin}/auth?page=${page}&size=${size}`, { params: comnUtils.toGetParams(data) }),
    getOgaUsrDtl: (data: any) => api.get(`${BASE.api}/${data.tin}`),
    getOgaUsrAuthList: (data: any) => api.get(`${BASE.api}/${data.tin}/usr-auth`),
    getOgaUsrAuthHistList: (tin: any) => api.get(`${BASE.api}/${tin}/auth-hist`),
    saveOgaUsrAuthList: ({ tin, ...rest }: any) => api.post(`${BASE.api}/${tin}/usr-auth`, rest),
};

export const URLS = {
    osci0104020q: `${BASE.path}/osci0104020q`, // OGA User Auth Management List !== OGA 사용자 권한 관리 목록 ==!
    osci0104021s: `${BASE.path}/osci0104021s`, // Save. OGA User Auth Management !== OGA 사용자 권한 저장 ==!
};

export const SF_OGA_USR_SRCH: TFormSchema = {
    id: "form_orgUsrSrch",
    schema: {
        orgCd: { type: "code", label: "L_ORG_CD", area: "orgCd", popupSize: "md" },
        deptCd: { type: "select", label: "L_DEPT_CD" },
        usrNm: { type: "text", label: "L_USR_NM" },
        tin: { type: "text", label: "L_TIN" },
    },
};

export const SF_AUTH_SRCH: TFormSchema = {
    id: "form_authSrch",
    schema: {
        tin: { type: "text" },
        authId: { type: "text", label: "L_AUTH_ID" },
        authNm: { type: "text", label: "L_AUTH_NM" },
    },
};

export const SF_OGA_USR_INFO: TFormSchema = {
    id: "form_ogaUsrInfo",
    schema: {
        tin: { type: "text", label: "L_TIN", edit: false },
        usrNm: { type: "text", label: "L_USR_NM", edit: false },
        ogaCd: { type: "text", label: "L_OGA_CD", edit: false },
        ogaNm: { type: "text", label: "L_OGA_NM", edit: false },
        ogaDeptCd: { type: "text", label: "L_OGA_DEPT_CD", edit: false },
        ogaDeptNm: { type: "text", label: "L_OGA_DEPT_NM", edit: false },
        usrMoblNo: { type: "text", label: "L_USR_MOBL_NO", edit: false },
        usrEmlAddr: { type: "text", label: "L_USR_EML_ADDR", edit: false },
        usrRsdtAddr: { type: "text", label: "L_USR_RSDT_ADDR", edit: false },
        ogaTelno: { type: "text", label: "L_OGA_TELNO", edit: false },
        ogaFaxNo: { type: "text", label: "L_OGA_FAXNO", edit: false },
        ogaUrlAddr: { type: "text", label: "L_OGA_URL_ADDR", edit: false },
        ogaPostAddr: { type: "text", label: "L_OGA_POST_ADDR", edit: false },
    },
};

export const SG_OGA_USR_LIST: TGridSchema = {
    id: "grid_ogaUsrList",
    options: { index: true, pagination: "out" },
    head: [
        { cells: [{ header: "L_OGA_CD", binding: "ogaCd", width: "15%" }] },
        { cells: [{ header: "L_OGA_NM", binding: "ogaNm", width: "20%" }] },
        { cells: [{ header: "L_OGA_DEPT_NM", binding: "ogaDeptNm", width: "*" }] },
        { cells: [{ header: "L_TIN", binding: "tin", width: "15%" }] },
        { cells: [{ header: "L_USR_NM", binding: "usrNm", width: "20%" }] },
    ],
    body: [
        { cells: [{ binding: "ogaCd", align: "center" }] },
        { cells: [{ binding: "ogaNm", align: "left" }] },
        { cells: [{ binding: "ogaDeptNm", align: "left" }] },
        { cells: [{ binding: "tin", align: "center" }] },
        { cells: [{ binding: "usrNm", align: "left" }] },
    ],
};

export const SG_USR_AUTH_LIST: TGridSchema = {
    id: "grid_usrAuthList",
    options: { checkbox: true, height: 250 },
    head: [
        { cells: [{ header: "L_AUTH_ID", binding: "authId", width: "0.5*" }] },
        { cells: [{ header: "L_AUTH_NM", binding: "authNm", width: "0.5*" }] },
        { cells: [{ header: "L_AUTH_DESC", binding: "authDesc", width: "0.8*" }] },
        { cells: [{ header: "L_FRST_REGST_ID", binding: "frstRegstId", width: "0.5*" }] },
    ],
    body: [
        { cells: [{ binding: "authId", align: "center" }] },
        { cells: [{ binding: "authNm", align: "left" }] },
        { cells: [{ binding: "authDesc", align: "left" }] },
        { cells: [{ binding: "frstRegstId", align: "center" }] },
    ],
};

export const SG_USR_AUTH_HIST_LIST: TGridSchema = {
    id: "grid_usrAuthHistList",
    head: [
        { cells: [{ header: "L_AUTH_ID", binding: "authId", width: "0.5*" }] },
        { cells: [{ header: "L_AUTH_NM", binding: "authNm", width: "0.5*" }] },
        { cells: [{ header: "L_AUTH_DESC", binding: "authDesc", width: "0.8*" }] },
        { cells: [{ header: "L_HIST_SRNO", binding: "histSrno", width: "0.3*" }] },
        { cells: [{ header: "L_DEL_YN", binding: "delYn", width: "0.2*" }] },
        { cells: [{ header: "L_FRST_REGST_ID", binding: "frstRegstId", width: "0.5*" }] },
        { cells: [{ header: "L_FRST_RGSR_DTM", binding: "frstRgsrDtm", width: "0.5*" }] },
    ],
    body: [
        { cells: [{ binding: "authId", align: "center" }] },
        { cells: [{ binding: "authNm", align: "left" }] },
        { cells: [{ binding: "authDesc", align: "left" }] },
        { cells: [{ binding: "histSrno", align: "center" }] },
        { cells: [{ binding: "delYn", align: "center" }] },
        { cells: [{ binding: "frstRegstId", align: "center" }] },
        { cells: [{ binding: "frstRgsrDtm", align: "center" }] },
    ],
};

export const SG_SCRH_AUTH_LIST: TGridSchema = {
    id: "grid_srchAuthList",
    options: { index: true, checkbox: true, pagination: "out", height: 250 },
    head: [
        { cells: [{ header: "L_AUTH_ID", binding: "authId", width: "*" }] },
        { cells: [{ header: "L_AUTH_NM", binding: "authNm", width: "*" }] },
        { cells: [{ binding: "isRgsrYn", width: "*" }], show: false },
    ],
    body: [
        { cells: [{ binding: "authId", align: "center" }] },
        { cells: [{ binding: "authNm", align: "left" }] },
        { cells: [{ binding: "isRgsrYn", align: "center" }] },
    ],
};
